import { useState, useRef, useEffect } from 'react'
import InfiniteViewer from 'react-infinite-viewer';
import Selecto from 'react-selecto'

import { Frame, useEditor } from '@craftjs/core'
import { CodeIcon, LayoutGridIcon } from 'lucide-react';
import { cn, ToggleOptionGroup, ToggleOptionGroupItem } from 'twyne-new.ui'

import { CodeEditor } from '@/components/CodeEditor';
import { GlobalStateProvider, StateToolbarButton } from '@/components/builder/utils/State';

import BuilderConfig from '../../BuilderConfig';
import { useBuilder, useInteractionMode, useResponsive } from '../context';

import BreadcrumbsBlocks from './BreadcrumbsBlocks';
import FloatingToolbar from './FloatingToolbar';
import TailwindSandbox from './TailwindSandbox';

export default function BuilderContent() {
  const [view, setView] = useState('builder')
  const { width, height, deviceHeight } = useResponsive()
  const { value: interactionMode } = useInteractionMode()
  const [zoom, setZoom] = useState(false)

  return <GlobalStateProvider>
    <div className='h-2 z-10 twy-bg-base' />
    <div className='twy-bg-base-accent relative flex gap-2 p-2 pb-0! items-center justify-end z-10 overflow-hidden'>
      <BreadcrumbsBlocks />
      <div className='flex gap-2'>
        <StateToolbarButton />
        <ToggleOptionGroup type='single' size='lg' defaultValue='builder' onValueChange={setView}>
          <ToggleOptionGroupItem value='builder' className='flex items-center gap-2'>
            <LayoutGridIcon size={16} />
            Builder
          </ToggleOptionGroupItem>
          <ToggleOptionGroupItem value='code' className='flex items-center gap-2'>
            <CodeIcon size={16} />
            Code
          </ToggleOptionGroupItem>
        </ToggleOptionGroup>
        <BuilderConfig />
      </div>
    </div>
    <div className={`p-2 twy-bg-base-accent flex-1 relative ${view !== 'builder' ? 'hidden' : ''}`}>
      <Viewer zoom={zoom} onZoom={setZoom}>
        <TailwindSandbox
          className={cn(interactionMode === "move" || zoom ? "pointer-events-none" : "")}
          htmlClassName='w-full h-full p-6 bg-gray-50!'
          bodyClassName='w-full h-full bg-white'
          width={width}
          height={height}
          deviceHeight={deviceHeight}
        >
          <Content deviceHeight={deviceHeight} />
        </TailwindSandbox>
      </Viewer>
      <FloatingToolbar />
    </div>
    <div className={`twy-bg-base flex-1 mx-2 ${view !== 'code' ? 'hidden' : ''}`}>
      <HTML />
    </div>
    <div className='h-2 z-10 twy-bg-base' />
  </GlobalStateProvider>
}

function Content({ deviceHeight }) {
  const { actions: { selectNode }, nodes, hovered } = useEditor(state => ({
    nodes: state.nodes,
    hovered: state.events.hovered,
  }))
  const { value: interactionMode, setValue: setInteractionMode } = useInteractionMode()

  const frameRef = useRef(null)
  const [selectable, setSelectable] = useState([])

  useEffect(() => {
    const selectable = Object.values(nodes).map(node => node.dom).filter(dom => dom)
    setSelectable(selectable)
  }, [nodes])

  const handleSelect = event => {
    const nodesEntries = Object.entries(nodes)
    const selecteds = event.selected.map(element => nodesEntries.find(([, node]) => node.dom === element)?.[0])
    selectNode(selecteds)
  }

  const handleDragStart = event => {
    if (interactionMode !== 'select') {
      event.stop()
    }

    const targets = [...hovered]

    if (targets.length && !targets.includes('ROOT')) {
      event.stop()
    }
  }

  return <>
    <div ref={frameRef} className={cn('w-full @container-size', deviceHeight ? `h-[${deviceHeight}px]` : '')}>
      <Frame />
    </div>
    {interactionMode === 'select' ? <Selecto
      container={frameRef.current}
      hitRate={100}
      selectByClick={true}
      selectFromInside={true}
      toggleContinueSelect={["ctrl"]}
      keyContainer={window}
      selectableTargets={selectable}
      onDragStart={handleDragStart}
      onDragEnd={() => setInteractionMode('')}
      onSelect={handleSelect}
    /> : null}
  </>
}

function Viewer({ children, zoom, onZoom }) {
  const { value: interactionMode } = useInteractionMode()

  const viewerRef = useRef(null)
  const zoomRef = useRef({})

  useEffect(() => {
    const handleKeyDown = event => {
      if (event.ctrlKey && !zoomRef.current.zoom) {
        onZoom('zoom')
        zoomRef.current.zoom = true
      }
    }

    const handleKeyUp = event => {
      if (!event.ctrlKey && zoomRef.current.zoom) {
        onZoom(false)
        zoomRef.current.zoom = false
      }
    }

    const handleClick = () => {
      if (zoomRef.current.zoom) {
        onZoom(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)
    window.addEventListener('click', handleClick)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
      window.removeEventListener('click', handleClick)
    }
  }, [onZoom])

  useEffect(() => {
    if (viewerRef.current) {
      const frame = requestAnimationFrame(() => {
        viewerRef.current.setZoom(0.59, { zoomOffsetX: 50, zoomOffsetY: 50 })
      })
      return () => cancelAnimationFrame(frame)
    }
  }, [])

  const mouseDrag = interactionMode === 'move'
  const classes = INTERACTION_CLASSES[interactionMode]

  return <InfiniteViewer
    id='builder-viewer'
    ref={viewerRef}
    className={cn("w-full h-full bg-gray-100", classes, zoom ? 'cursor-zoom-in' : '')}
    rangeX={[0, 0]}
    rangeY={[0, 0]}
    rangeOffsetX={[-500, 5000]}
    rangeOffsetY={[-100, 10000]}
    zoomRange={[0.1, 5]}
    wheelScale={0.001}
    useAutoZoom={true}
    useMouseDrag={mouseDrag}
  >
    {children}
  </InfiniteViewer>
}

function HTML() {
  const { html } = useBuilder()

  return <>
    <CodeEditor
      language="html"
      value={html}
      width='100%'
      height='100%'
      readOnly
    />
  </>
}

const INTERACTION_CLASSES = {
  select: 'cursor-crosshair select-none',
  move: 'cursor-grab select-none',
  'format_painter:past': 'cursor-copy',
}
